import { cn } from '../../lib/cn.ts';

export interface SparklineProps {
  /** Series values, oldest → newest. */
  data: number[];
  width?: number;
  height?: number;
  /** Stroke token class, default charcoal. */
  strokeClassName?: string;
  /** Soft area fill under the line. */
  fill?: boolean;
  /**
   * Stretch to the parent's full width. `width`/`height` still set the
   * drawing coordinate system; the stroke stays uniform via
   * `vector-effect: non-scaling-stroke`.
   */
  stretch?: boolean;
  /** Screen-reader summary of the trend (the chart itself is aria-hidden). */
  ariaLabel?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Sparkline — a tiny "printed line drawing" of a metric trend. Purely
 * decorative (aria-hidden); a screen-reader summary is provided via `ariaLabel`.
 */
export function Sparkline({
  data,
  width = 96,
  height = 28,
  strokeClassName = 'stroke-[var(--color-charcoal)]',
  fill = false,
  stretch = false,
  ariaLabel,
  className,
  'data-testid': testId,
}: SparklineProps) {
  const pad = 2;
  const w = width - pad * 2;
  const h = height - pad * 2;
  const min = data.length ? Math.min(...data) : 0;
  const max = data.length ? Math.max(...data) : 1;
  const span = max - min || 1;
  const stepX = data.length > 1 ? w / (data.length - 1) : 0;
  const points = data.map((v, i) => {
    const x = pad + i * stepX;
    const y = pad + h - ((v - min) / span) * h;
    return [x, y] as const;
  });
  const line = points
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
  const lastPoint = points[points.length - 1];
  const firstPoint = points[0];
  const area =
    points.length > 1 && lastPoint && firstPoint
      ? `${line} L${lastPoint[0].toFixed(2)} ${(pad + h).toFixed(2)} L${firstPoint[0].toFixed(2)} ${(pad + h).toFixed(2)} Z`
      : '';

  return (
    <span data-testid={testId} className={cn(stretch ? 'flex w-full' : 'inline-flex', className)}>
      <svg
        aria-hidden="true"
        width={stretch ? '100%' : width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio={stretch ? 'none' : undefined}
        fill="none"
      >
        {fill && area && <path d={area} className="fill-[var(--color-stone-200)] opacity-50" />}
        <path
          d={line}
          className={cn('fill-none', strokeClassName)}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect={stretch ? 'non-scaling-stroke' : undefined}
        />
      </svg>
      {ariaLabel && <span className="sr-only">{ariaLabel}</span>}
    </span>
  );
}
